import React, {useState} from "react";
import {
	isAuthorizedByPermission,
	pageAuthorizedByPermission
} from "../../fragments/PageAuthorization";
import {
	Autocomplete,
	Box, Breadcrumbs,
	Button,
	Checkbox,
	CircularProgress, Dialog, DialogContent, DialogContentText, DialogTitle, FormControl,
	FormControlLabel, InputLabel, MenuItem,
	Paper, Select,
	Stack,
	TextField,
	Typography
} from "@mui/material";
import {graphQLServices} from "../../../services/GraphQLServices";
import {translateFunction} from "../../../localization/TranslationService";
import {
	AuthQueries, POLICIES_INDEX,
	POLICY_USAGES, POLICY_USAGES_INDEX
} from "../../../backend/authorization/AuthQueries";
import {AuthPolicyUsage} from "../../../backend/authorization/models/AuthPolicyUsage";
import {useQuery} from "@apollo/client";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import {debouncer} from "../../../libraries/util/Debounce";
import {AuthPolicy} from "../../../backend/authorization/models/AuthPolicy";
import {ProcessButton} from "../../../components/common/input/ProcessButton";
import {permissions} from "../../../global/Permissions";
import {Delete} from "@mui/icons-material";
import {deletePolicyUsage, putPolicyUsage} from "../Functions";
import {Link, useSearchParams} from "react-router-dom";
import {PentaSplit} from "../../../components/common/PentaSplit";

const translate = translateFunction();

const searchDebouncer = debouncer(400);

export const PolicyUsagesOverviewPage = pageAuthorizedByPermission(function () {
	const [params] = useSearchParams();
	const [search, setSearch] = useState<string>(params.get("search") ?? "");
	
	const editable = isAuthorizedByPermission(permissions.authorizationPutPolicyUsage);
	const deletable = isAuthorizedByPermission(permissions.authorizationDeletePolicyUsage);
	
	function computeQuery(search: string) {
		if (!search)
			return "";
		return `?search=${encodeURIComponent(search)}`;
	}
	
	const basePath = "/authorization/policyusages";
	function updateUrl(search: string) {
		const query = computeQuery(search);
		window.history.replaceState(null, "", `${basePath}${query}`);
	}
	
	return <PentaSplit
		top={<Breadcrumbs style={{margin: "5px"}}>
			<Link style={{color: "inherit", textDecoration: "unset"}}
				to="/authorization">{translate("Authorization")}</Link>
			<Typography color="text.primary">{translate("Policy Usages")}</Typography>
		</Breadcrumbs>}
		center={<Stack spacing={1}>
			<TextField
				label={"Search"}
				defaultValue={search}
				onChange={event => searchDebouncer(() => {
					const search = event.target.value;
					setSearch(search);
					updateUrl(search);
				})}
			/>
			
			<Results search={search} editable={editable} deletable={deletable}/>
		</Stack>}
	/>;
}, [permissions.authorizationRead]);

interface ResultProps {
	readonly search: string;
	readonly editable: boolean;
	readonly deletable: boolean;
}

function computeFilter(search: string): any | null {
	if (!search)
		return null;
	
	const searchTerms = search.split(" ")
		.filter(it => !!it);
	
	if (searchTerms.length === 0)
		return null;
	
	return {
		and: searchTerms.map(term => {
			if (term === "sync")
				return {isSynchronized: {eq: true}};
			if (term === "!sync")
				return {isSynchronized: {eq: false}};
			return {
				or: [
					{policies: {some: {contains: term}}},
					{krn: {contains: term}},
					{userName: {contains: term}},
					{organisationId: {contains: term}},
					{roleName: {contains: term}},
				]
			};
		})
	};
}

function Results(props: ResultProps) {
	const policiesIndexQuery = useQuery<AuthQueries>(POLICIES_INDEX, {
		client: graphQLServices.authorizationClient,
		fetchPolicy: "no-cache"
	});
	
	const policiesIndex: AuthPolicy[] = policiesIndexQuery.data?.policies.nodes ?? [];
	
	const policyUsagesQuery = useQuery<AuthQueries>(POLICY_USAGES, {
		client: graphQLServices.authorizationClient,
		variables: {
			where: computeFilter(props.search),
			order: {krn: "ASC"}
		},
		fetchPolicy: "no-cache"
	});
	const policyUsagesIndexQuery = useQuery<AuthQueries>(POLICY_USAGES_INDEX, {
		client: graphQLServices.authorizationClient,
		fetchPolicy: "no-cache"
	});
	
	
	const error = policyUsagesQuery.error ?? policiesIndexQuery.error ?? policyUsagesIndexQuery.error;
	if (error)
		throw error;
	
	if (policyUsagesQuery.loading)
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	
	const policyUsages: AuthPolicyUsage[] = policyUsagesQuery.data?.policyUsages.nodes ?? [];
	
	const policyNamesByKrn: Map<string, string> = policiesIndex.associateBy(it => it.krn, it => it.name);
	
	const policyUsagesIndex: string[] = policyUsagesIndexQuery.data?.policyUsages.nodes.map(it => it.krn) ?? [];
	
	return <Paper style={{
		width: "100%",
		maxHeight: "100%"
	}}>
		<Stack style={{
			height: "100%"
		}}>
			<Stack direction={"row"} padding={"16px 16px 0 16px"} justifyContent={"space-between"}>
				<Typography variant={"h6"}>Usages</Typography>
				<NewPolicyUsageDialog
					disabled={!props.editable}
					existingPolicyUsages={new Set<string>(policyUsagesIndex)}
					updateGrid={policyUsagesQuery.refetch}
				/>
			</Stack>
			<TableContainer>
				{policyUsages.length === 0
					? <Typography paddingBottom={"16px"} paddingLeft={"16px"}>No matches found</Typography>
					: <Table>
						<TableBody>
							{policyUsages.map(usage => {
								return <PolicyUsageRow
									key={usage.id}
									usage={usage}
									policiesIndex={policiesIndex}
									policyNamesByKrn={policyNamesByKrn}
									editable={props.editable}
									deletable={props.deletable}
								/>;
							})}
						</TableBody>
					</Table>}
			</TableContainer>
		</Stack>
	</Paper>;
}

interface NewPolicyUsageDialogProps {
	readonly disabled: boolean;
	readonly existingPolicyUsages: Set<string>;
	readonly updateGrid: () => void;
}

function NewPolicyUsageDialog(props: NewPolicyUsageDialogProps) {
	const [open, setOpen] = useState(false);
	
	const [name, setName] = useState<string>("");
	const [tenant, setTenant] = useState<string>("kizi");
	const [isSynchronized, setIsSynchronized] = useState<boolean>(false);
	const [error, setError] = useState<string>("");
	
	function handleClickOpen() {
		setOpen(true);
	}
	
	function handleClose() {
		setOpen(false);
	}
	
	async function handleCreate() {
		const krn = `krn:${tenant}:auth-policy-usage:${name}`;
		if (props.existingPolicyUsages.has(krn)) {
			throw new Error("A policy usage for this tenant with this name already exists.");
		} else {
			await putPolicyUsage(null, {
				id: crypto.randomUUID(),
				krn,
				tenant,
				name,
				userName: null,
				organisationId: null,
				roleName: null,
				policies: [],
				isSynchronized
			});
			
			setOpen(false);
			props.updateGrid();
		}
	}
	
	return <>
		<Button
			variant={"outlined"}
			disabled={props.disabled}
			onClick={handleClickOpen}
		>{translate("Add")}</Button>
		<Dialog open={open} onClose={handleClose}>
			<DialogTitle>{translate("New Policy Usage")}</DialogTitle>
			<DialogContent>
				<DialogContentText>
					{translate("Please provide the following information to create a new policy usage.")}
				</DialogContentText>
				<FormControl margin={"dense"} fullWidth
					error={!!error}>
					<InputLabel id={"tenant-label"}>{translate("Tenant")}</InputLabel>
					<Select
						labelId={"tenant-label"}
						label={translate("Tenant")}
						variant={"standard"}
						value={tenant}
						onChange={event => {
							const value = event.target.value;
							setTenant(value);
							
							const krn = `krn:${value}:auth-policy-usage:${name}`;
							if (props.existingPolicyUsages.has(krn)) {
								setError("A policy usage for this tenant with this name already exists.");
							} else {
								setError("");
							}
						}}
					>
						<MenuItem key={"kizi"} value={"kizi"}>{"kizi"}</MenuItem>
						<MenuItem key={"risk"} value={"risk"}>{"risk"}</MenuItem>
					</Select>
				</FormControl>
				<TextField
					error={!!error}
					helperText={error ? translate(error) : "​"}
					value={name}
					autoFocus
					margin="dense"
					label={translate("Name")}
					fullWidth
					variant="standard"
					required={true}
					onChange={event => {
						const value = event.target.value;
						setName(value);
						
						if (/^[a-z0-9.-]*$/.test(value)) {
							setError("");
						} else {
							setError("Invalid characters (value must be full lowercase)");
						}
						
						const krn = `krn:${tenant}:auth-policy-usage:${value}`;
						if (props.existingPolicyUsages.has(krn)) {
							setError("A policy usage for this tenant with this name already exists.");
						} else {
							setError("");
						}
					}}
				/>
				<FormControlLabel
					label={translate("Is synchronized")}
					control={<Checkbox
						checked={isSynchronized}
						onChange={event => {
							setIsSynchronized(event.target.checked);
						}}
					/>}
				/>
				<Stack direction={"row"} justifyContent={"space-between"} style={{marginTop: "0px"}}>
					<Button onClick={handleClose}>{translate("Cancel")}</Button>
					<ProcessButton
						defaultText={"Create"}
						completedText={"Created"}
						inProgressText={"Creating"}
						onClick={handleCreate}
						buttonProps={{
							disabled: !!error
						}}
					/>
				</Stack>
			</DialogContent>
		</Dialog>
	</>;
}

interface PolicyUsageRowProps {
	readonly usage: AuthPolicyUsage;
	readonly policiesIndex: AuthPolicy[];
	readonly policyNamesByKrn: Map<string, string>;
	readonly editable: boolean;
	readonly deletable: boolean;
}

function PolicyUsageRow(props: PolicyUsageRowProps) {
	const policiesIndex = props.policiesIndex;
	const policyNamesByKrn = props.policyNamesByKrn;
	
	const [usage, setUsage] = useState(props.usage);
	const [userNameError, setUserNameError] = useState<string>("");
	const [organizationIdError, setOrganizationIdError] = useState<string>("");
	const [roleNameError, setRoleNameError] = useState<string>("");
	
	return <TableRow>
		<TableCell>
			<ProcessButton
				defaultText={"Save"}
				completedText={"Saved"}
				inProgressText={"Saving"}
				onClick={() => handleSave(usage)}
				resetKeys={usage}
				buttonProps={{
					disabled: !props.deletable
				}}
			/>
			<ProcessButton
				startIcon={<Delete/>}
				defaultText={"Delete"}
				completedText={"Deleted"}
				inProgressText={"Deleting"}
				onClick={() => handleDelete(usage)}
				resetKeys={usage}
				buttonProps={{
					disabled: !props.editable
				}}
			/>
		</TableCell>
		<TableCell>
			<TextField
				variant={"standard"}
				label={"KRN"}
				value={usage.krn}
				disabled
				style={{
					minWidth: "300px"
				}}
			/>
		</TableCell>
		<TableCell>
			<TextField
				variant={"standard"}
				label={"Username"}
				value={usage.userName ?? ""}
				error={!!userNameError}
				helperText={translate(userNameError)}
				onChange={event => {
					const value = event.target.value;
					setUsage({...usage, userName: value || null});
					
					if (/^[a-z0-9.-]*$/.test(value)) {
						setUserNameError("");
					} else {
						setUserNameError("Invalid characters (value must be full lowercase)");
					}
				}}
				disabled={!props.editable}
				style={{
					minWidth: "200px"
				}}
			/>
		</TableCell>
		<TableCell>
			<TextField
				variant={"standard"}
				label={"Organization ID"}
				value={usage.organisationId ?? ""}
				error={!!organizationIdError}
				helperText={translate(organizationIdError)}
				onChange={event => {
					const value = event.target.value;
					setUsage({...usage, organisationId: value || null});
					
					if (/^[a-z0-9.-]*$/.test(value)) {
						setOrganizationIdError("");
					} else {
						setOrganizationIdError("Invalid characters (value must be full lowercase)");
					}
				}}
				disabled={!props.editable}
				style={{
					minWidth: "200px"
				}}
			/>
		</TableCell>
		<TableCell>
			<TextField
				variant={"standard"}
				label={"Role Name"}
				value={usage.roleName ?? ""}
				error={!!roleNameError}
				helperText={translate(roleNameError)}
				onChange={event => {
					const value = event.target.value;
					setUsage({...usage, roleName: value || null});
					
					if (/^[a-z0-9.-]*$/.test(value)) {
						setRoleNameError("");
					} else {
						setRoleNameError("Invalid characters (value must be full lowercase)");
					}
				}}
				disabled={!props.editable}
				style={{
					minWidth: "200px"
				}}
			/>
		</TableCell>
		<TableCell>
			<FormControlLabel
				label={translate("Is synchronized")}
				control={<Checkbox
					checked={usage.isSynchronized}
					onChange={event => {
						setUsage({...usage, isSynchronized: event.target.checked});
					}}
				/>}
			/>
		</TableCell>
		<TableCell>
			<Autocomplete<AuthPolicy, true>
				multiple
				options={policiesIndex.filter(it => it.tenant === usage.tenant)}
				disabled={!props.editable || policiesIndex.length === 0}
				isOptionEqualToValue={(option, value) => option.krn === value.krn}
				value={usage.policies.map(krn => ({krn, name: policyNamesByKrn.get(krn) ?? "..."} as AuthPolicy))}
				renderInput={(params) => <TextField
					{...params}
					label={translate("Policies")}
					variant={"standard"}
					style={{
						minWidth: "200px"
					}}
				/>}
				onChange={(_, items) => {
					setUsage({...usage, policies: items.map(it => it.krn)});
				}}
				limitTags={3}
				disableCloseOnSelect
				getOptionLabel={(option) => option.name}
				renderOption={(props, option, {selected}) => (
					<li {...props}>
						<FormControlLabel
							control={<Checkbox checked={selected}/>}
							label={option.name}
						/>
					</li>
				)}
			/>
		</TableCell>
	</TableRow>;
}

async function handleSave(policyUsage: AuthPolicyUsage) {
	await putPolicyUsage(null, policyUsage);
}

async function handleDelete(policyUsage: AuthPolicyUsage) {
	await deletePolicyUsage(null, policyUsage.krn);
}

export function openAuthPolicyUsageDetailsFor(krn: string) {
	window.location.href = linkAuthPolicyUsageDetailsFor(krn);
}

export function linkAuthPolicyUsageDetailsFor(krn: string): string {
	return `/authorization/policyusages?search=${encodeURIComponent(krn)}`;
}
